import React from 'react';
import { FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { EDIT_BLOG_V2, EDIT_PAGE_V2 } from '@confluence/named-routes/entry-points/routes.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';

import { ALLOWED_FLAG_MESSAGES } from './allowedFlagConstants.ts';
import { getPageHistoryLink } from './getPageHistoryLink.tsx';

const i18n = {
	blogifySuccessTitle: {
		id: 'editor-flags.blogify.blogpost.success.title',
		defaultMessage: 'Page converted to a blog post',
		description:
			'The title of the flag shown in the editor when someone convert a page to blogpost',
	},
	blogifySuccessMessage: {
		id: 'editor-flags.blogify.blogpost.success.message',
		defaultMessage:
			'{displayName} just converted this page to a <alink>blog post</alink>, which includes the changes you’ve made so far.',
		description: 'The body of the flag shown in the editor when someone convert a page to blogpost',
	},
	restoreTitle: {
		id: 'editor-flags.restore-page.title',
		defaultMessage: 'Previous version restored',
		description:
			'Title for flag that appears when another user restores a previous version of the page',
	},
	restoreMessage: {
		id: 'editor-flags.restore.page.success.message',
		defaultMessage:
			'Recent changes may have been overwritten. Check the <alink>page history</alink> for details.',
		description:
			'The body of the flag shown in the editor when someone revert the page/blogpost to previous version in page history',
	},
	extensionCreateTitle: {
		id: 'editor-flags.extension-create.title',
		defaultMessage: 'New draft created',
		description:
			'Title for flag that appears when a draft is created by the /create Confluence page extension from another page',
	},
	extensionCreateMessage: {
		id: 'editor-flags.extension-create.message',
		defaultMessage:
			'There’s now a link to this draft on <alink>{parentPage}</alink>. All of your changes have been saved.',
		description:
			'The body of the flag shown in the editor when a draft is created by the /create Confluence page extension from another page',
	},
	extensionCreateParentPage: {
		id: 'editor-flags.extension-create.parent-page',
		defaultMessage: 'the previous page',
		description:
			'Placeholder text for {parentPage} in `editor-flags.extension-create.message` when the parent page does not have a title',
	},
};

export const initializeSuccessFlag = (
	flags: FlagsStateContainer,
	formatMessage: (...args: any[]) => any,
	{ attributes, flagData },
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
) => {
	const { contentType, spaceKey, contentId } = attributes;
	if (flagData) {
		if (flagData[ALLOWED_FLAG_MESSAGES.RESTORE_CONTENT_SUCCESS]) {
			void flags.showFlag({
				type: 'success-circle',
				title: formatMessage(i18n.restoreTitle),
				description: (
					<FormattedMessage
						{...i18n.restoreMessage}
						values={{
							contentType,
							alink: getPageHistoryLink(attributes),
						}}
					/>
				),
				messageType: 'transactional',
				messageId: 'editor-flags-version-restore-success',
			});
		} else if (flagData[ALLOWED_FLAG_MESSAGES.BLOGIFY_BLOGPOST_SUCCESS]) {
			const displayName = flagData?.converterDisplayName;
			void flags.showFlag({
				type: 'success-circle',
				title: formatMessage(i18n.blogifySuccessTitle),
				description: (
					<FormattedMessage
						{...i18n.blogifySuccessMessage}
						values={{
							displayName:
								displayName ||
								formatMessage({
									id: 'editor.page.moved.message.unknown.mover',
								}),
							alink: (...chunks) => (
								<Link
									target="_blank"
									href={EDIT_BLOG_V2.toUrl(
										{
											spaceKey,
											contentId,
											contentType: 'blog',
										},
										{
											query: {
												draftShareId: getDraftShareIdFromQueryParam(),
											},
										},
									)}
								>
									{chunks}
								</Link>
							),
						}}
					/>
				),
				messageType: 'transactional',
				messageId: 'editor-flags-blogify-success',
			});
		} else if (flagData[ALLOWED_FLAG_MESSAGES.CREATED_FROM_EXTENSION]) {
			const { parentContentId, parentTitle } = flagData;
			const parentUrl = EDIT_PAGE_V2.toUrl({
				contentType: 'pages',
				spaceKey,
				contentId: parentContentId,
			});
			const handleClick = (event) => {
				event.preventDefault();
				// Open the parent page in the existing tab if it hasn't been closed
				const parentWin = window.open('', parentContentId);
				parentWin?.focus();
				// If tab was closed, navigate new tab to the parent page URL
				if (parentWin && !parentWin.location.host) {
					// eslint-disable-next-line no-restricted-syntax
					parentWin.location.assign(parentUrl);
				}

				if (createAnalyticsEvent) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'parentPageLink',
							actionSubjectId: 'inlineCreateOneStepExperiment',
							source: 'SuccessFlag',
							attributes: {
								contentId,
								contentType,
								parentContentId,
							},
						},
					}).fire();
				}
			};

			void flags.showFlag({
				type: 'success-circle',
				title: formatMessage(i18n.extensionCreateTitle),
				description: (
					<FormattedMessage
						{...i18n.extensionCreateMessage}
						values={{
							parentPage: !!parentTitle
								? parentTitle
								: formatMessage(i18n.extensionCreateParentPage),
							alink: (...chunks) => (
								<Link href={parentUrl} target={parentContentId} onClick={handleClick}>
									{chunks}
								</Link>
							),
						}}
					/>
				),
				messageType: 'transactional',
				messageId: 'editor-flags-create-from-extension-success',
			});
		}
	}
};

/**
 * Helper function to get the parent page contentId from a URL parameter when a page was
 * created from the /create Confluence page extension
 */
export const getParentContentId = () => {
	// createdFrom parameter means draft was created by the create page extension from another page
	const urlParams = new URLSearchParams(window.location.search);
	const parentContentId = urlParams.get('createdFrom');

	if (parentContentId) {
		// Remove createdFrom param from url without reloading
		urlParams.delete('createdFrom');
		const newParams = urlParams.size > 0 ? `?${urlParams.toString()}` : '';
		window.history.pushState({}, '', `${window.location.pathname}${newParams}`);

		// Show flag if the parentContentId is a number
		// and this was an auto redirect from parent page, i.e. not copy/pasted url
		if (/^\d+$/.test(parentContentId) && !!document.referrer) {
			return parentContentId;
		}
	}

	return null;
};
